import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getExpenses, createExpense, updateExpense, deleteExpense, getExpenseCategories, ocrExpense, exportExpensesExcel, getProviders } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import AiCreateButton from '../components/ai/AiCreateButton';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Camera, Upload, Receipt, Trash2, Edit2, X, Loader2,
  TrendingDown, Wallet, FileSpreadsheet, Package, Calendar, Users,
  ArrowUpDown,
} from 'lucide-react';
import { StatCard, FilterChip, FilterRow, FiltersCard, SurfaceCard, ResetFiltersButton } from '../components/ui/filters-toolkit';

const CATEGORY_LABELS = {
  proveedores: "Proveedores",
  combustible: "Combustible",
  comidas: "Comidas y Dietas",
  material_oficina: "Material de Oficina",
  alquiler: "Alquiler",
  seguros: "Seguros",
  transporte: "Transporte",
  software: "Software",
  telefonia: "Telefonía",
  formacion: "Formación",
  suministros: "Suministros",
  vehiculo: "Vehículo",
  otros: "Otros"
};

const CATEGORY_COLORS = {
  proveedores: "bg-blue-100 text-blue-700",
  combustible: "bg-amber-100 text-amber-700",
  comidas: "bg-orange-100 text-orange-700",
  material_oficina: "bg-cyan-100 text-cyan-700",
  alquiler: "bg-purple-100 text-purple-700",
  seguros: "bg-emerald-100 text-emerald-700",
  transporte: "bg-rose-100 text-rose-700",
  software: "bg-indigo-100 text-indigo-700",
  telefonia: "bg-teal-100 text-teal-700",
  formacion: "bg-pink-100 text-pink-700",
  suministros: "bg-lime-100 text-lime-700",
  vehiculo: "bg-sky-100 text-sky-700",
  otros: "bg-stone-100 text-stone-700"
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('expenses:sortBy') || 'expense_date_desc'; } catch { return 'expense_date_desc'; }
  });
  const [ocrLoading, setOcrLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [formData, setFormData] = useState({
    vendor: '', vendor_tax_id: '', category: 'otros', description: '',
    amount: '', tax_rate: '21', tax_amount: '', total: '',
    expense_date: new Date().toISOString().split('T')[0],
    receipt_image_url: '', notes: '', provider_id: ''
  });

  const fetchExpenses = useCallback(async () => {
    try {
      const [res, provRes] = await Promise.all([
        getExpenses({}),
        getProviders().catch(() => ({ data: [] })),
      ]);
      setExpenses(res.data);
      setProviders(provRes.data || []);
    } catch (err) {
      toast.error('Error cargando gastos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleExportExcel = async () => {
    try {
      toast.info('Exportando gastos...');
      await exportExpensesExcel();
      toast.success('Gastos exportados');
    } catch {
      toast.error('Error al exportar gastos');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor: '', vendor_tax_id: '', category: 'otros', description: '',
      amount: '', tax_rate: '21', tax_amount: '', total: '',
      expense_date: new Date().toISOString().split('T')[0],
      receipt_image_url: '', notes: '', provider_id: ''
    });
    setEditingExpense(null);
  };

  const openModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        vendor: expense.vendor || '',
        vendor_tax_id: expense.vendor_tax_id || '',
        category: expense.category || 'otros',
        description: expense.description || '',
        amount: String(expense.amount || ''),
        tax_rate: String(expense.tax_rate || '21'),
        tax_amount: String(expense.tax_amount || ''),
        total: String(expense.total || ''),
        expense_date: expense.expense_date || new Date().toISOString().split('T')[0],
        receipt_image_url: expense.receipt_image_url || '',
        notes: expense.notes || '',
        provider_id: expense.provider_id || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const calcTotals = (field, value) => {
    const data = { ...formData, [field]: value };
    const amount = parseFloat(data.amount) || 0;
    const taxRate = parseFloat(data.tax_rate) || 0;
    
    if (field === 'total' && value) {
      const total = parseFloat(value) || 0;
      const base = total / (1 + taxRate / 100);
      data.amount = base.toFixed(2);
      data.tax_amount = (total - base).toFixed(2);
    } else {
      data.tax_amount = (amount * taxRate / 100).toFixed(2);
      data.total = (amount + parseFloat(data.tax_amount)).toFixed(2);
    }
    setFormData(data);
  };

  const handleOcr = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    try {
      const res = await ocrExpense(file);
      const d = res.data.extracted_data;
      setFormData(prev => ({
        ...prev,
        vendor: d.vendor || prev.vendor,
        vendor_tax_id: d.vendor_tax_id || prev.vendor_tax_id,
        category: d.category || prev.category,
        description: d.description || prev.description,
        amount: d.amount ? String(d.amount) : prev.amount,
        tax_rate: d.tax_rate ? String(d.tax_rate) : prev.tax_rate,
        tax_amount: d.tax_amount ? String(d.tax_amount) : prev.tax_amount,
        total: d.total ? String(d.total) : prev.total,
        expense_date: d.expense_date || prev.expense_date,
        receipt_image_url: d.receipt_image_url || res.data.image_url || ''
      }));
      if (res.data.error) {
        toast.warning('Foto guardada, pero no se pudieron extraer todos los datos. Completa manualmente.');
      } else {
        toast.success('Datos extraídos de la foto correctamente');
      }
      setShowModal(true);
    } catch (err) {
      toast.error('Error procesando la imagen');
    } finally {
      setOcrLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.vendor || !formData.total) {
      toast.error('Proveedor y total son obligatorios');
      return;
    }
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        tax_rate: parseFloat(formData.tax_rate) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        total: parseFloat(formData.total) || 0
      };
      if (editingExpense) {
        await updateExpense(editingExpense.id, payload);
        toast.success('Gasto actualizado');
      } else {
        await createExpense(payload);
        toast.success('Gasto registrado');
      }
      setShowModal(false);
      resetForm();
      fetchExpenses();
    } catch (err) {
      toast.error('Error guardando el gasto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      await deleteExpense(id);
      toast.success('Gasto eliminado');
      fetchExpenses();
    } catch (err) {
      toast.error('Error eliminando gasto');
    }
  };

  const years = useMemo(() => {
    const set = new Set(expenses.map((e) => (e.expense_date || '').slice(0, 4)).filter(Boolean));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [expenses]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const out = expenses.filter((e) => {
      if (filterCategory !== 'all' && e.category !== filterCategory) return false;
      if (filterYear !== 'all' && !(e.expense_date || '').startsWith(filterYear)) return false;
      if (filterProvider !== 'all' && e.vendor !== filterProvider) return false;
      if (q) {
        const hay = `${e.vendor || ''} ${e.vendor_tax_id || ''} ${e.description || ''} ${e.notes || ''} ${e.category || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Apply sort
    const cmp = (a, b, key, dir) => {
      const va = a?.[key] ?? '';
      const vb = b?.[key] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), 'es', { numeric: true });
    };
    const [field, direction] = sortBy.endsWith('_asc') ? [sortBy.replace('_asc', ''), 1] : [sortBy.replace('_desc', ''), -1];
    return [...out].sort((a, b) => cmp(a, b, field, direction));
  }, [expenses, searchQuery, filterCategory, filterYear, filterProvider, sortBy]);

  const totals = useMemo(() => filtered.reduce((acc, e) => ({
    total: acc.total + (e.total || 0),
    tax: acc.tax + (e.tax_amount || 0),
    count: acc.count + 1,
  }), { total: 0, tax: 0, count: 0 }), [filtered]);

  const topCategory = useMemo(() => {
    const counts = {};
    filtered.forEach((e) => { counts[e.category || 'otros'] = (counts[e.category || 'otros'] || 0) + (e.total || 0); });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0] || null;
  }, [filtered]);

  const providerNames = useMemo(() => {
    const set = new Set(expenses.map((e) => e.vendor).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [expenses]);

  const activeFiltersCount = [
    filterCategory !== 'all',
    filterYear !== 'all',
    filterProvider !== 'all',
    searchQuery.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchQuery(''); setFilterCategory('all'); setFilterYear('all'); setFilterProvider('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="expenses-title">Gastos Deducibles</h1>
            <p className="text-stone-500 text-sm mt-1">Registra y controla tus gastos</p>
          </div>
          <PageHelpButton moduleKey="expenses" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button variant="outline" className="rounded-xl flex-1 sm:flex-none" onClick={handleExportExcel} data-testid="export-expenses-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => e.target.files[0] && handleOcr(e.target.files[0])}
          />
          <input
            type="file"
            ref={cameraInputRef}
            className="hidden"
            accept="image/*"
            capture="environment"
            onChange={(e) => e.target.files[0] && handleOcr(e.target.files[0])}
          />
          <Button
            onClick={() => cameraInputRef.current?.click()}
            variant="outline"
            className="rounded-xl flex-1 sm:flex-none"
            disabled={ocrLoading}
            data-testid="camera-scan-button"
          >
            {ocrLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
            <span className="sm:inline">Escanear</span>
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="rounded-xl flex-1 sm:flex-none"
            disabled={ocrLoading}
            data-testid="upload-receipt-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            <span className="sm:inline">Subir Foto</span>
          </Button>
          <Link to="/import?entity=expenses" className="flex-1 sm:flex-none">
            <Button variant="outline" className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 w-full" data-testid="import-expenses-button">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
          </Link>
          <Button
            onClick={() => openModal()}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 flex-1 sm:flex-none"
            data-testid="add-expense-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Manual
          </Button>
          <AiCreateButton entity="expense" className="flex-1 sm:flex-none" />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="expenses-stats">
        <StatCard
          label="Total gastos"
          value={formatCurrency(totals.total)}
          sub={`${totals.count} gasto${totals.count === 1 ? '' : 's'}`}
          icon={TrendingDown}
          gradient="bg-gradient-to-br from-rose-50 to-red-50"
          iconBg="bg-gradient-to-br from-rose-500 to-red-500"
          testid="total-expenses-card"
        />
        <StatCard
          label="IVA soportado"
          value={formatCurrency(totals.tax)}
          sub="Deducible AEAT"
          icon={Wallet}
          gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="tax-deductible-card"
        />
        <StatCard
          label="Categoría top"
          value={topCategory ? CATEGORY_LABELS[topCategory[0]] || topCategory[0] : '—'}
          sub={topCategory ? formatCurrency(topCategory[1]) : 'Sin datos'}
          icon={Package}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="top-category-card"
        />
        <StatCard
          label="Promedio"
          value={totals.count > 0 ? formatCurrency(totals.total / totals.count) : formatCurrency(0)}
          sub="Por gasto"
          icon={Receipt}
          gradient="bg-gradient-to-br from-sky-50 to-blue-50"
          iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
          testid="avg-expense-card"
        />
      </div>

      {/* Filter card */}
      <FiltersCard testid="expenses-filters-card">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <Input
              placeholder="Buscar por proveedor, NIF, concepto o categoría…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
              data-testid="search-expenses-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          {providerNames.length > 0 && (
            <div className="relative lg:w-64">
              <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
              <Select value={filterProvider} onValueChange={setFilterProvider}>
                <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="expenses-provider-filter">
                  <SelectValue placeholder="Todos los proveedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proveedores</SelectItem>
                  {providerNames.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="relative lg:w-56">
            <ArrowUpDown className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select
              value={sortBy}
              onValueChange={(v) => {
                setSortBy(v);
                try { localStorage.setItem('expenses:sortBy', v); } catch {}
              }}
            >
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="expenses-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense_date_desc">Más recientes primero</SelectItem>
                <SelectItem value="expense_date_asc">Más antiguos primero</SelectItem>
                <SelectItem value="total_desc">Importe (mayor→menor)</SelectItem>
                <SelectItem value="total_asc">Importe (menor→mayor)</SelectItem>
                <SelectItem value="vendor_asc">Proveedor (A→Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {activeFiltersCount > 0 && (
            <ResetFiltersButton count={activeFiltersCount} onClick={resetFilters} testid="expenses-reset-filters" />
          )}
        </div>

        {years.length > 0 && (
          <FilterRow label="Año" testid="expenses-year-filters">
            <FilterChip active={filterYear === 'all'} onClick={() => setFilterYear('all')} testid="expenses-year-chip-all">Todos</FilterChip>
            {years.map((y) => (
              <FilterChip key={y} active={filterYear === y} onClick={() => setFilterYear(y)} testid={`expenses-year-chip-${y}`}>{y}</FilterChip>
            ))}
          </FilterRow>
        )}

        <FilterRow label="Categoría" testid="expenses-category-filters">
          <FilterChip active={filterCategory === 'all'} onClick={() => setFilterCategory('all')} testid="filter-category-all">Todas</FilterChip>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <FilterChip key={k} active={filterCategory === k} onClick={() => setFilterCategory(k)} testid={`filter-category-${k}`}>{v}</FilterChip>
          ))}
        </FilterRow>
      </FiltersCard>

      {/* Expenses List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-12 text-center" data-testid="no-expenses">
          <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-600 mb-2">Sin gastos registrados</h3>
          <p className="text-stone-400 text-sm mb-4">Escanea una factura o añade un gasto manualmente</p>
          <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300">
            <Plus className="w-4 h-4 mr-2" /> Añadir Gasto
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((expense) => (
            <div
              key={expense.id}
              className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-stone-300 transition-colors"
              data-testid={`expense-item-${expense.id}`}
            >
              <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  {expense.receipt_image_url ? (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-stone-100 flex items-center justify-center overflow-hidden">
                      <img src={expense.receipt_image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-stone-100 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-stone-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{expense.vendor}</p>
                    <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-xl font-medium ${CATEGORY_COLORS[expense.category] || CATEGORY_COLORS.otros}`}>
                      {CATEGORY_LABELS[expense.category] || expense.category}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 truncate mt-0.5">
                    {expense.provider_name && <span className="text-[#FF6B4A] font-medium">{expense.provider_name} · </span>}
                    {expense.description || expense.notes || 'Sin descripción'} · {expense.expense_date}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                <div className="text-right">
                  <p className="font-mono font-semibold text-sm text-red-600">{formatCurrency(expense.total)}</p>
                  <p className="text-[10px] text-stone-400">IVA: {formatCurrency(expense.tax_amount)}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openModal(expense)} className="p-1.5 hover:bg-stone-100 rounded-xl" data-testid={`edit-expense-${expense.id}`}>
                    <Edit2 className="w-3.5 h-3.5 text-stone-500" />
                  </button>
                  <button onClick={() => handleDelete(expense.id)} className="p-1.5 hover:bg-red-50 rounded-xl" data-testid={`delete-expense-${expense.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="expense-modal">
          <div className="bg-white rounded-xl w-full max-w-lg my-4 sm:my-0">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-stone-100">
              <h2 className="text-lg font-heading font-bold">
                {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1 hover:bg-stone-100 rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Proveedor *</Label>
                  <Input value={formData.vendor} onChange={(e) => setFormData(p => ({ ...p, vendor: e.target.value }))} placeholder="Nombre del proveedor" className="rounded-xl" data-testid="expense-vendor-input" />
                </div>
                <div>
                  <Label>NIF/CIF</Label>
                  <Input value={formData.vendor_tax_id} onChange={(e) => setFormData(p => ({ ...p, vendor_tax_id: e.target.value }))} placeholder="B12345678" className="rounded-xl" data-testid="expense-taxid-input" />
                </div>
              </div>
              {providers.length > 0 && (
                <div>
                  <Label>Vincular a proveedor registrado</Label>
                  <Select value={formData.provider_id || undefined} onValueChange={(v) => {
                    const prov = providers.find(p => p.id === v);
                    setFormData(p => ({
                      ...p, provider_id: v,
                      vendor: prov?.name || p.vendor,
                      vendor_tax_id: prov?.tax_id || p.vendor_tax_id
                    }));
                  }}>
                    <SelectTrigger className="rounded-xl" data-testid="expense-provider-select">
                      <Package className="w-4 h-4 mr-2 text-stone-400" />
                      <SelectValue placeholder="Seleccionar proveedor (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.tax_id ? ` (${p.tax_id})` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Categoría</Label>
                  <Select value={formData.category || undefined} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="rounded-xl" data-testid="expense-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={formData.expense_date} onChange={(e) => setFormData(p => ({ ...p, expense_date: e.target.value }))} className="rounded-xl" data-testid="expense-date-input" />
                </div>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Descripción del gasto" className="rounded-xl" data-testid="expense-description-input" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label>Base</Label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => calcTotals('amount', e.target.value)} className="rounded-xl font-mono" data-testid="expense-amount-input" />
                </div>
                <div>
                  <Label>IVA %</Label>
                  <Input type="number" value={formData.tax_rate} onChange={(e) => calcTotals('tax_rate', e.target.value)} className="rounded-xl font-mono" data-testid="expense-taxrate-input" />
                </div>
                <div>
                  <Label>IVA</Label>
                  <Input type="number" step="0.01" value={formData.tax_amount} readOnly className="rounded-xl font-mono bg-stone-50" data-testid="expense-taxamount-input" />
                </div>
                <div>
                  <Label>Total *</Label>
                  <Input type="number" step="0.01" value={formData.total} onChange={(e) => calcTotals('total', e.target.value)} className="rounded-xl font-mono font-bold" data-testid="expense-total-input" />
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Notas adicionales" className="rounded-xl" rows={2} data-testid="expense-notes-input" />
              </div>
              {formData.receipt_image_url && (
                <div className="border border-stone-100 rounded-xl p-2">
                  <p className="text-xs text-stone-500 mb-1">Justificante adjunto</p>
                  <img src={formData.receipt_image_url} alt="Recibo" className="max-h-32 object-contain rounded" />
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end p-4 sm:p-6 border-t border-stone-100">
              <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="rounded-xl" data-testid="cancel-expense-button">
                Cancelar
              </Button>
              <Button onClick={handleSubmit} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="save-expense-button">
                {editingExpense ? 'Actualizar' : 'Guardar Gasto'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
